import { EsPCAReportField, PCAReportMetric } from 'c-sdk';

export type RowStats = Record<string, any>;
export type AnalyticsLeaderboardDataRow = Record<string, any>;

export const COMPARISON_SERIES_SUFFIX = '__comparison';
export const DSP_AUDIENCE_FILTER_KEY = 'audience_display_name';
export const DSP_NO_AUDIENCE_VALUE = 'nothing';

export const DSP_ENDPOINT_ALLOWED_FILTERS = new Set<string>([
    DSP_AUDIENCE_FILTER_KEY,
    'audience_version',
    'display_owner',
    'display_format',
    'display_owner_format',
    'display_type',
    'scheduled',
    'data_status',
]);

export const parseNum = (v: any) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

export const compareSortValues = (aVal: string | number, bVal: string | number) => {
    if (typeof aVal === 'number' && typeof bVal === 'number') return aVal - bVal;
    return String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
};

export const isObject = (v: any) => v && typeof v === 'object' && !Array.isArray(v);

export const isGroupedStatsLeaf = (value: any) =>
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    [
        'display_owner',
        'display_format',
        'sum_of_plays',
        'sum_of_play',
        'impacts',
        'geopath_audience',
        'expected_impacts_in_schedule',
        'dsp_impressions',
        'geopath_impressions',
        'variance',
    ].some(key => key in value);

export const OTHER_GROUP_LABEL = 'Other';

const isBadDspGroupKey = (value: unknown): boolean => {
    const trimmed = String(value ?? '').trim();
    return trimmed === '' || /^["|]+$/.test(trimmed);
};
export const normalizeDspGroupKey = (value: unknown): string =>
    isBadDspGroupKey(value) ? OTHER_GROUP_LABEL : String(value ?? '');

export const foldDspGroupLabel = (label: unknown): string => {
    const str = String(label ?? '');
    if (isBadDspGroupKey(str)) return OTHER_GROUP_LABEL;
    const segments = str.split(/\s*[|-]\s*/);
    if (segments.length > 1 && segments.every(segment => isBadDspGroupKey(segment))) {
        return OTHER_GROUP_LABEL;
    }
    return str;
};

const DSP_ADDITIVE_STAT_FIELDS = new Set([
    'sum_of_plays',
    'sum_of_play',
    'sum_of_time',
    'impacts',
    'dsp_impressions',
    'geopath_impressions',
    'geopath_audience',
    'display_count',
    'kilowatts',
    'kgco2',
    'expected_plays_in_schedule',
    'expected_time_in_schedule',
    'expected_impacts_in_schedule',
    'plays_received_in_schedule',
    'plays_received_out_of_schedule',
    'plays_received_unbooked',
    'time_received_in_schedule',
    'time_received_out_of_schedule',
    'time_received_unbooked',
    'impacts_received_in_schedule',
    'impacts_received_out_of_schedule',
    'impacts_received_unbooked',
]);

const dspPlaysOf = (stats: any): number =>
    Number(stats?.sum_of_plays ?? stats?.sum_of_play ?? 0) || 0;

const DSP_DIMENSION_FIELDS = [
    'display_owner',
    'display_format',
    'display_owner_format',
    'display_format_name',
    'display_town',
    'display_tv_region',
    'display_conurbation',
    'display_env',
    'display_line_item_name',
    'display_screen_name_summary',
    'display_resolution',
    'creative_name',
];

const relabelLeafDimensions = (leaf: any): any => {
    if (!leaf || typeof leaf !== 'object') return leaf;
    let out = leaf;
    DSP_DIMENSION_FIELDS.forEach(fieldName => {
        if (fieldName in out && isBadDspGroupKey(out[fieldName])) {
            if (out === leaf) out = { ...leaf };
            out[fieldName] = OTHER_GROUP_LABEL;
        }
    });
    return out;
};

const mergeDspLeaves = (a: any, b: any): any => {
    const out: Record<string, any> = { ...a };
    const aPlays = dspPlaysOf(a);
    const bPlays = dspPlaysOf(b);
    const totalPlays = aPlays + bPlays;
    new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})]).forEach(key => {
        const av = (a ?? {})[key];
        const bv = (b ?? {})[key];
        const aNum = typeof av === 'number' && Number.isFinite(av);
        const bNum = typeof bv === 'number' && Number.isFinite(bv);
        if (DSP_ADDITIVE_STAT_FIELDS.has(key)) {
            out[key] = (aNum ? av : 0) + (bNum ? bv : 0);
        } else if (aNum || bNum) {
            out[key] =
                totalPlays > 0
                    ? ((aNum ? av : 0) * aPlays + (bNum ? bv : 0) * bPlays) / totalPlays
                    : (aNum ? av : 0) + (bNum ? bv : 0);
        } else if (!(key in out)) {
            out[key] = bv;
        }
    });
    return out;
};

const mergeDspNodes = (a: any, b: any): any => {
    if (isGroupedStatsLeaf(a) && isGroupedStatsLeaf(b)) return mergeDspLeaves(a, b);
    if (
        a &&
        b &&
        typeof a === 'object' &&
        typeof b === 'object' &&
        !Array.isArray(a) &&
        !Array.isArray(b)
    ) {
        const out: Record<string, any> = { ...a };
        Object.entries(b).forEach(([key, value]) => {
            out[key] = key in out ? mergeDspNodes(out[key], value) : value;
        });
        return out;
    }
    return a;
};

export const foldGroupedDspStats = (node: any): any => {
    if (!node || typeof node !== 'object') return node;

    if (isGroupedStatsLeaf(node)) return relabelLeafDimensions(node);

    if (Array.isArray(node)) {
        const byKey = new Map<string, any>();
        node.forEach((item, idx) => {
            const folded = isGroupedStatsLeaf(item) ? relabelLeafDimensions(item) : item;
            const dims =
                folded && typeof folded === 'object'
                    ? DSP_DIMENSION_FIELDS.filter(fieldName => fieldName in folded)
                    : [];
            const key = dims.length
                ? dims.map(fieldName => normalizeDspGroupKey(folded[fieldName])).join(' ')
                : `__row_${idx}`;
            const existing = byKey.get(key);
            byKey.set(key, existing ? mergeDspNodes(existing, folded) : folded);
        });
        return Array.from(byKey.values());
    }

    const out: Record<string, any> = {};
    Object.entries(node).forEach(([key, child]) => {
        const normalizedKey = normalizeDspGroupKey(key);
        const foldedChild = foldGroupedDspStats(child);
        out[normalizedKey] =
            normalizedKey in out ? mergeDspNodes(out[normalizedKey], foldedChild) : foldedChild;
    });
    return out;
};

export const getComparisonSeriesKey = (baseKey: string) => `${baseKey}${COMPARISON_SERIES_SUFFIX}`;
export const isComparisonSeriesKey = (seriesKey: string) =>
    seriesKey.endsWith(COMPARISON_SERIES_SUFFIX);
export const getBaseSeriesKey = (seriesKey: string) =>
    isComparisonSeriesKey(seriesKey)
        ? seriesKey.slice(0, -COMPARISON_SERIES_SUFFIX.length)
        : seriesKey;

export const getOwnerFormatLabel = (stats: RowStats, fallback: string) => {
    const owner = stats?.display_owner ?? stats?.displayOwner ?? stats?.owner ?? null;
    const format = stats?.display_format ?? stats?.displayFormat ?? stats?.format ?? null;
    if (owner && format) return `${owner} - ${format}`;
    return fallback;
};

export const getBucketGroupLabel = (bucket: any, field?: EsPCAReportField): string => {
    const owner = bucket?.display_owner;
    const format = bucket?.display_format;

    if (field === EsPCAReportField.DisplayOwnerFormat) {
        if (owner && format) return `${owner} - ${format}`;
        return owner ?? format ?? '';
    }

    if (field === EsPCAReportField.DisplayOwner) return owner ?? '';
    if (field === EsPCAReportField.DisplayFormat) return format ?? '';

    if (field && bucket?.[field as any] != null && bucket?.[field as any] !== '') {
        return String(bucket[field as any]);
    }

    return '';
};

export const getGroupKey = (groupField: EsPCAReportField | undefined, item: any, idx: number) => {
    const direct = groupField ? item?.[groupField as any] : undefined;
    if (direct != null && direct !== '') return direct;

    if (item?.key != null && item.key !== '') return item.key;
    if (item?.name != null && item.name !== '') return item.name;
    if (item?.value != null && item.value !== '') return item.value;

    if (groupField && item?.field === groupField && item?.value) return item.value;

    return String(idx);
};

export const pickGroupedPayload = (resp: any) =>
    resp?.data ?? resp?.reportData ?? resp?.report_data ?? resp?.buckets ?? resp ?? {};

export const pickAuditMultipliersPayload = (resp: any) =>
    resp?.auditMultipliers ??
    resp?.audit_multipliers ??
    resp?.auditMultipliersData ??
    resp?.data?.auditMultipliers ??
    resp?.data?.audit_multipliers ??
    resp?.data?.auditMultipliersData ??
    resp?.data?.data?.auditMultipliers ??
    resp?.data?.data?.audit_multipliers ??
    resp?.data?.data?.auditMultipliersData ??
    resp?.reportData?.auditMultipliers ??
    resp?.report_data?.auditMultipliers ??
    null;

export const pickMultipliersPayload = (resp: any) =>
    resp?.multipliers ??
    resp?.multipliersData ??
    resp?.data?.multipliers ??
    resp?.data?.multipliersData ??
    resp?.data?.data?.multipliers ??
    resp?.data?.data?.multipliersData ??
    resp?.reportData?.multipliers ??
    resp?.report_data?.multipliers ??
    null;

export const pickBuckets = (
    resp: any,
    field?: EsPCAReportField,
    fields?: EsPCAReportField[],
): any[] => {
    if (Array.isArray(resp?.buckets)) return resp.buckets;
    if (Array.isArray(resp?.data)) return resp.data;
    if (Array.isArray(resp?.histogram)) return resp.histogram;

    const isMulti = Array.isArray(fields) && fields.length > 1;

    if (resp?.data && typeof resp.data === 'object') {
        if (isMulti) {
            const out: any[] = [];
            const fieldArr = fields as EsPCAReportField[];
            const walk = (node: any, depth: number, parts: string[], dateKey: string) => {
                if (!node || typeof node !== 'object') return;
                if (depth >= fieldArr.length || isGroupedStatsLeaf(node)) {
                    out.push({
                        ...node,
                        key_as_string: dateKey,
                        group: parts.join(' | '),
                    });
                    return;
                }
                Object.entries(node).forEach(([k, v]) => {
                    walk(v, depth + 1, [...parts, String(k)], dateKey);
                });
            };
            Object.entries(resp.data).forEach(([dateKey, groupObj]) => {
                walk(groupObj, 0, [], dateKey);
            });
            return out;
        }

        return Object.entries(resp.data).flatMap(([dateKey, groupObj]: any) => {
            if (!groupObj || typeof groupObj !== 'object') return [];
            return Object.entries(groupObj).flatMap(([groupKey, stats]: any) => {
                if (!stats || typeof stats !== 'object' || Array.isArray(stats)) return [];

                if (isGroupedStatsLeaf(stats)) {
                    return [
                        {
                            ...stats,
                            key_as_string: dateKey,
                            group:
                                getBucketGroupLabel(stats, field) ||
                                stats?.display_owner ||
                                stats?.display_format ||
                                String(groupKey),
                        },
                    ];
                }

                return Object.entries(stats).map(([nestedKey, nestedStats]: any) => ({
                    ...nestedStats,
                    key_as_string: dateKey,
                    group:
                        getBucketGroupLabel(nestedStats, field) ||
                        nestedStats?.display_owner ||
                        nestedStats?.display_format ||
                        String(nestedKey),
                }));
            });
        });
    }

    return [];
};

export const pickDateKey = (bucket: any): string =>
    bucket?.key_as_string ??
    bucket?.keyAsString ??
    bucket?.date ??
    bucket?.date_key ??
    bucket?.key ??
    bucket?.start_date ??
    '';

export const pickGroupKey = (bucket: any, field?: EsPCAReportField): string => {
    const bucketGroupLabel = getBucketGroupLabel(bucket, field);
    if (bucketGroupLabel) return bucketGroupLabel;
    if (bucket?.group != null && bucket.group !== '') return bucket.group;
    if (bucket?.name != null && bucket.name !== '') return bucket.name;
    if (bucket?.key != null && bucket.key !== '') return bucket.key;
    return '';
};

export const pickDeliveredImpacts = (stats: RowStats) =>
    stats?.impacts_received_in_schedule ??
    stats?.impacts ??
    stats?.impressions ??
    stats?.delivered_impressions ??
    stats?.dsp_impressions ??
    stats?.actual ??
    0;

export const pickGeopathAudience = (stats: RowStats) =>
    stats?.geopath_audience ?? stats?.geopath_impressions ?? stats?.geopath ?? 0;

export const pickAverageAudience = (stats: RowStats) =>
    stats?.dsp_average_impressions ?? stats?.dsp_average_impacts ?? 0;

export const pickPlays = (stats: RowStats) =>
    stats?.plays_received_in_schedule ??
    stats?.sum_of_plays ??
    stats?.sum_of_play ??
    stats?.plays ??
    0;

export const pickTimeSeconds = (stats: RowStats) =>
    stats?.time_received_in_schedule ??
    stats?.sum_of_time ??
    stats?.time_seconds ??
    stats?.time ??
    0;

export const pickMetricValue = (bucket: any, metric: PCAReportMetric) => {
    if (metric === PCAReportMetric.Plays) return pickPlays(bucket);
    if (metric === PCAReportMetric.Time) return pickTimeSeconds(bucket);
    return pickDeliveredImpacts(bucket);
};

export const pickActualByMetric = (stats: RowStats, metric: PCAReportMetric) => {
    if (metric === PCAReportMetric.Plays) return pickPlays(stats);
    if (metric === PCAReportMetric.Time) return pickTimeSeconds(stats);
    return pickDeliveredImpacts(stats);
};

export const mergeAvailableFilters = (
    primary?: Record<string, any> | null,
    secondary?: Record<string, any> | null,
) => ({
    ...(secondary ?? {}),
    ...(primary ?? {}),
});

const omitAudienceFilters = (filters?: Record<string, any>) => {
    const next = { ...(filters ?? {}) };
    delete next[DSP_AUDIENCE_FILTER_KEY];
    delete next.audience_version;
    return next;
};

export const normalizeLeaderboardRow = (
    groupField: EsPCAReportField,
    item: any,
    idx: number,
    fallbacks: { owner?: string; format?: string; key?: string } = {},
): AnalyticsLeaderboardDataRow => {
    const row = item && typeof item === 'object' ? { ...item } : {};

    if (fallbacks.owner != null && row.display_owner == null) row.display_owner = fallbacks.owner;
    if (fallbacks.format != null && row.display_format == null) {
        row.display_format = fallbacks.format;
    }

    if (groupField !== EsPCAReportField.DisplayOwnerFormat) {
        const fallbackKey = fallbacks.key ?? getGroupKey(groupField, row, idx);
        if (row[groupField as any] == null || row[groupField as any] === '') {
            row[groupField as any] = fallbackKey;
        }
    }

    return row;
};

export const getRowFieldValue = (row: AnalyticsLeaderboardDataRow, field: EsPCAReportField) => {
    if (field === EsPCAReportField.DisplayOwnerFormat) {
        const owner = row.display_owner ?? row.displayOwner ?? row.owner ?? '';
        const format = row.display_format ?? row.display_format_name ?? row.displayFormat ?? '';
        return format ? `${owner}__${format}` : owner;
    }

    return row[field as any] ?? row.key ?? row.name ?? row.value ?? '';
};

export const getGeoVals = (stats: any) => {
    const dsp = parseNum(stats?.dsp_impressions ?? stats?.impacts ?? 0);
    const geo = parseNum(stats?.geopath_impressions ?? stats?.geopath_audience ?? 0);
    const variance = stats?.variance != null ? parseNum(stats?.variance) : parseNum(geo - dsp);
    const performancePct = dsp > 0 ? (geo / dsp) * 100 : 0;
    return { dsp, geo, variance, performancePct };
};

export const getAverageAudienceVals = (stats: any) => {
    const dsp = parseNum(stats?.impacts ?? stats?.dsp_impressions ?? stats?.impressions ?? 0);
    const avg = parseNum(stats?.dsp_average_impressions ?? stats?.dsp_average_impacts ?? 0);
    const variance = avg - dsp;
    const performancePct = dsp > 0 ? (avg / dsp) * 100 : 0;
    return { dsp, avg, variance, performancePct };
};
